import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { Logout, Edit, Delete, Add, ArrowBack } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const StatsManagement = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedStat, setSelectedStat] = useState(null);
  const [formData, setFormData] = useState({
    icon: 'fa-user-graduate',
    value: 0,
    suffix: '',
    label: '',
    labelAr: '',
    description: '',
    descriptionAr: '',
    gradient: 'linear-gradient(90deg, #0f4c81, #0f4c81)',
    textColor: '#0f4c81',
    iconColor: 'white',
    bgColor: 'white'
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.getStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleBackToDashboard = () => {
    navigate('/admin/dashboard');
  };

  const handleOpenDialog = (stat = null) => {
    if (stat) {
      setFormData({
        icon: stat.icon || 'fa-user-graduate',
        value: stat.value || 0,
        suffix: stat.suffix || '',
        label: stat.label || '',
        labelAr: stat.labelAr || '',
        description: stat.description || '',
        descriptionAr: stat.descriptionAr || '',
        gradient: stat.gradient || 'linear-gradient(90deg, #0f4c81, #0f4c81)',
        textColor: stat.textColor || '#0f4c81',
        iconColor: stat.iconColor || 'white',
        bgColor: stat.bgColor || 'white'
      });
      setSelectedStat(stat);
      setIsEditing(true);
    } else {
      setFormData({
        icon: 'fa-user-graduate',
        value: 0,
        suffix: '',
        label: '',
        labelAr: '',
        description: '',
        descriptionAr: '',
        gradient: 'linear-gradient(90deg, #0f4c81, #0f4c81)',
        textColor: '#0f4c81',
        iconColor: 'white',
        bgColor: 'white'
      });
      setSelectedStat(null);
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedStat(null);
  };

  const handleOpenDeleteDialog = (stat) => {
    setSelectedStat(stat);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedStat(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedStat) {
        // Si on édite une statistique existante
        await api.updateStat(selectedStat._id, formData);
      } else {
        // Si on crée une nouvelle statistique
        await api.createStat(formData);
      }
      
      // Rafraîchir la liste des statistiques
      fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving stat:', error);
    }
  };

  const handleDeleteStat = async () => {
    try {
      await api.deleteStat(selectedStat._id);
      // Rafraîchir la liste des statistiques
      fetchStats();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting stat:', error);
    }
  };

  // Liste d'icônes Font Awesome populaires
  const iconOptions = [
    'fa-user-graduate',
    'fa-building',
    'fa-chart-line',
    'fa-map-marked-alt',
    'fa-users',
    'fa-briefcase',
    'fa-graduation-cap',
    'fa-certificate',
    'fa-award',
    'fa-handshake',
    'fa-globe',
    'fa-school'
  ];

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      bgcolor: '#f5f5f5'
    }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <IconButton 
            color="inherit" 
            edge="start" 
            onClick={handleBackToDashboard}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Gestion des Statistiques
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<Logout />}>
            Déconnexion
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Liste des Statistiques</Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Ajouter une statistique
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Icône</TableCell>
                    <TableCell>Valeur</TableCell>
                    <TableCell>Libellé (FR)</TableCell>
                    <TableCell>Libellé (AR)</TableCell>
                    <TableCell>Description (FR)</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.length > 0 ? (
                    stats.map((stat) => (
                      <TableRow key={stat._id}>
                        <TableCell>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%',
                            background: stat.gradient,
                            color: stat.iconColor
                          }}>
                            <i className={`fas ${stat.icon}`}></i>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {stat.value}{stat.suffix}
                        </TableCell>
                        <TableCell>{stat.label}</TableCell>
                        <TableCell>{stat.labelAr || '-'}</TableCell>
                        <TableCell>
                          {stat.description.length > 30 
                            ? `${stat.description.substring(0, 30)}...` 
                            : stat.description}
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleOpenDialog(stat)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleOpenDeleteDialog(stat)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Aucune statistique trouvée
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Dialogue d'ajout/modification de statistique */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {isEditing ? 'Modifier la statistique' : 'Ajouter une statistique'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Informations en français</Typography>
                <TextField
                  name="label"
                  label="Libellé"
                  value={formData.label}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Informations en arabe</Typography>
                <TextField
                  name="labelAr"
                  label="Libellé (Arabe)"
                  value={formData.labelAr}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  dir="rtl"
                />
                <TextField
                  name="descriptionAr"
                  label="Description (Arabe)"
                  value={formData.descriptionAr}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  dir="rtl"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="value"
                  label="Valeur"
                  type="number"
                  value={formData.value}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="suffix"
                  label="Suffixe (ex: %, +)"
                  value={formData.suffix}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="icon-select-label">Icône</InputLabel>
                  <Select
                    labelId="icon-select-label"
                    name="icon"
                    value={formData.icon}
                    label="Icône"
                    onChange={handleInputChange}
                  >
                    {iconOptions.map((icon) => (
                      <MenuItem key={icon} value={icon}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <i className={`fas ${icon}`} style={{ marginRight: '8px' }}></i>
                          {icon}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="gradient"
                  label="Dégradé (CSS)"
                  value={formData.gradient}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="textColor"
                  label="Couleur du texte"
                  value={formData.textColor}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  type="color"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="iconColor"
                  label="Couleur de l'icône"
                  value={formData.iconColor}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  type="color"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="bgColor"
                  label="Couleur de fond"
                  value={formData.bgColor}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  type="color"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid #ddd', 
                  borderRadius: 1,
                  mt: 2,
                  bgcolor: formData.bgColor
                }}>
                  <Typography variant="subtitle1" gutterBottom>Aperçu</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%',
                      background: formData.gradient,
                      color: formData.iconColor,
                      mr: 2
                    }}>
                      <i className={`fas ${formData.icon} fa-lg`}></i>
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ color: formData.textColor, fontWeight: 'bold' }}>
                        {formData.value}{formData.suffix}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: formData.textColor }}>
                        {formData.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: formData.textColor }}>
                        {formData.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained" color="primary">
              {isEditing ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer la statistique "{selectedStat?.label}" ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button onClick={handleDeleteStat} variant="contained" color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StatsManagement;
